import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";

/**
 * Resets scroll position on in-app navigation.
 *
 * `BrowserRouter` pushes a new history entry without touching scroll, so
 * following a mid-page link — a "Read more" in the news grid, a competition
 * card, a league table row — used to drop the user partway down the next route
 * instead of at its top. It also mounted that route's top ad strip above the
 * viewport, where it was requested but could never be seen.
 *
 * POP is left alone so the browser can restore the previous position on
 * back/forward, and anchor links keep their target.
 *
 * The reset is keyed on the pathname actually changing, not just on the effect
 * re-running: competitions rewrites its own query string with `history.replace`
 * on every filter change, which flips `history.action` without leaving the
 * route. Without the ref that would scroll the user to the top mid-search.
 */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const history = useHistory();
  const previousPathname = useRef(pathname);

  useEffect(() => {
    const leftTheRoute = previousPathname.current !== pathname;
    previousPathname.current = pathname;

    if (!leftTheRoute) return;
    if (history.action === "POP") return;
    if (hash) return;

    window.scrollTo(0, 0);
  }, [pathname, hash, history.action]);

  return null;
};
